import React, { useEffect, useRef, useState } from 'react';
import {
  Square,
  Button,
  SvgX,
  Heading,
  Row,
  Column,
  Stack,
  Flex,
  Loader,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Input } from 'src/components/Input/Input';
import { Modal } from 'src/components/Modal/Modal';
import { sdk } from 'src/utilities/sdk';
import { LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { ROLES } from 'src/types/Employee';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { EMAIL_REGEX } from 'src/utilities/regexPatterns';

interface IManagePersonModalProps {
  dinerId?: string;
  corpId: string;
  closeModal: () => void;
  onSuccess: () => void;
}

type FormFields = {
  firstName: string;
  lastName: string;
  email: string;
};

const emptyFormValues = {
  firstName: '',
  lastName: '',
  email: '',
};

interface DinerFields {
  first_name: string;
  last_name: string;
  email: string;
  role: ROLES;
}

export function ManagePersonModal({
  dinerId,
  corpId,
  closeModal,
  onSuccess,
}: IManagePersonModalProps): JSX.Element {
  const isEditing = !!dinerId;

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button variant="tertiary" type="button" onClick={closeModal}>
          <SvgX size="24px" />
        </Button>
        <Square inset="md">
          <Heading variant="h1">
            {isEditing ? "Edit this person's info" : 'Add a new person'}
          </Heading>
        </Square>
      </Square>
      <ManagePersonForm
        dinerId={dinerId}
        corpId={corpId}
        closeModal={closeModal}
        onSuccess={onSuccess}
      />
    </Modal>
  );
}

export interface IManagePersonFormProps extends IManagePersonModalProps {
  isPeopleLanding?: boolean;
}

export function ManagePersonForm({
  dinerId,
  corpId,
  closeModal,
  onSuccess,
  isPeopleLanding,
}: IManagePersonFormProps): JSX.Element {
  const { handleSubmit, register, errors, formState, setValue, getValues } =
    useForm<FormFields>({
      mode: 'onChange',
      defaultValues: emptyFormValues,
    });

  const [isLoadingDinerData, setIsLoadingDinerData] = useState(false);
  const { isSubmitting, isValid } = formState;
  const isEditing = !!dinerId;
  const dinerRole = useRef<ROLES>();
  const { sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    if (dinerId) {
      getDinerInfo(dinerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dinerId]);

  async function getDinerInfo(dinerId: string) {
    try {
      setIsLoadingDinerData(true);
      const { first_name, last_name, email, role } = await sdk.http.get(
        `/corp-admin/${corpId}/diners/${dinerId}`
      );

      setValue('firstName', first_name, { shouldValidate: true });
      setValue('lastName', last_name, { shouldValidate: true });
      setValue('email', email, { shouldValidate: true });
      dinerRole.current = role;
      setIsLoadingDinerData(false);
    } catch (error) {
      setIsLoadingDinerData(false);
      pushErrorFromAPI(error);
    }
  }

  async function onFormSubmit({
    email = getValues('email'),
    firstName,
    lastName,
  }: FormFields): Promise<void> {
    const dinerInfo: DinerFields = {
      email,
      first_name: firstName,
      last_name: lastName,
      role: dinerRole.current || ROLES.DINER,
    };

    try {
      if (isEditing) {
        await updateDiner(dinerInfo);
      } else {
        await addDiner(dinerInfo);

        sendTealiumEvent({
          eventcategory: isPeopleLanding
            ? 'self-service management tool_people'
            : 'self-service sign up_people',
          eventaction: 'add people_manually',
          eventlabel: 'success',
        });
      }

      // use a promise to allow form 'isSubmitting' to handle button disabled state
      await new Promise<void>((resolve) => {
        // add search delay in hopes of ensuring new data reaches the elastic search in time
        setTimeout(() => {
          pushSuccess(
            isEditing
              ? 'This person has been updated.'
              : 'This person has been added.'
          );

          onSuccess();
          resolve();
        }, LONG_SEARCH_DELAY);
      });
    } catch ({ status, data }) {
      const fallbackErrorMessage = isEditing
        ? 'There was a problem updating this person. Try again.'
        : 'There was a problem adding this person. Try again.';

      pushErrorFromAPI(
        {
          status,
          errors: data,
        },
        {
          fallbackMessage: fallbackErrorMessage,
          errorMessageCallback: (errorMessages) => {
            if (!isEditing) {
              sendTealiumEvent({
                eventcategory: isPeopleLanding
                  ? 'self-service management tool_people'
                  : 'self-service sign up_people',
                eventaction: 'add people_manually',
                eventlabel: `error_${errorMessages.join(',')}`,
              });
            }
          },
        }
      );
    }
  }

  async function addDiner(dinerInfo: DinerFields) {
    await sdk.http.post(`/corp-admin/${corpId}/diners`, dinerInfo);
  }

  async function updateDiner(dinerInfo: DinerFields) {
    await sdk.http.put(`/corp-admin/${corpId}/diners/${dinerId}`, {
      ...dinerInfo,
      id: dinerId,
    });
  }

  return (
    <>
      <Square inset="md" position="relative">
        {isLoadingDinerData && (
          <Flex
            backgroundColor="globals.white75a"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            marginTop="-16px"
            marginLeft="-16px"
            width="100%"
            height="100%"
            zIndex={10}
          >
            <Loader size="md" />
          </Flex>
        )}
        <form>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label="First name"
                  name="firstName"
                  id="firstName"
                  ref={register({
                    required: 'First name is required',
                  })}
                  error={errors.firstName}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label="Last name"
                  name="lastName"
                  id="lastName"
                  ref={register({
                    required: 'Last name is required',
                  })}
                  error={errors.lastName}
                />
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label="Email"
                  name="email"
                  id="email"
                  type="email"
                  disabled={isEditing}
                  ref={register({
                    required: 'Email is required',
                    pattern: {
                      value: EMAIL_REGEX,
                      message: 'Email is invalid',
                    },
                  })}
                  error={errors.email}
                />
              </Stack>
            </Column>
          </Row>
        </form>
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Row gutter={12}>
          <Column gutter={8} phone={6}>
            <Stack>
              <Button
                block
                variant="secondary"
                buttonSize="medium"
                disabled={isSubmitting}
                onClick={closeModal}
              >
                Cancel
              </Button>
            </Stack>
          </Column>
          <Column gutter={8} phone={6}>
            <Stack data-testid="add-button-wrapper">
              <Button
                block
                variant="primary"
                buttonSize="medium"
                disabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
                onClick={handleSubmit(onFormSubmit)}
              >
                {isEditing ? 'Update' : 'Add'}
              </Button>
            </Stack>
          </Column>
        </Row>
      </Square>
    </>
  );
}
